if (!customElements.get('nn-product-spin-viewer')) {
  customElements.define('nn-product-spin-viewer', class extends HTMLElement {
    connectedCallback() {
      this.image = this.querySelector('.nn-product-spin__image');
      this.status = this.querySelector('[data-spin-status]');
      this.frames = (this.dataset.frames || '').split('|').map((frame) => frame.trim()).filter(Boolean);
      if (!this.image || this.frames.length < 2) return;

      this.startIndex = Math.max(0, Math.min(this.frames.length - 1, Number(this.dataset.startIndex) || 0));
      this.index = this.startIndex;
      this.position = this.startIndex;
      this.dragging = false;
      this.moved = false;
      this.activePointerId = null;
      this.lastX = 0;
      this.lastMoveTime = 0;
      this.velocity = 0;
      this.renderFrame = null;
      this.inertiaFrame = null;
      this.preloaded = new Set([this.startIndex]);
      this.frameCache = window.__nnSpinFrameCache ||= new Map();
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.isProductCard = this.dataset.spinContext === 'product-card';

      window.addEventListener('pointerdown', this.onCapturedPointerDown, true);
      this.addEventListener('click', this.onClick, true);
      this.addEventListener('keydown', this.onKeyDown);
      this.addEventListener('dragstart', this.onDragStart);
      document.addEventListener('variant:update', this.reset);
      document.addEventListener('product:variant-change', this.reset);
      this.closest('product-information')?.addEventListener('change', this.onVariantInput);

      requestAnimationFrame(() => {
        const slideshow = this.closest('slideshow-component');
        slideshow?.select?.(0, undefined, { animate: false });
      });

      this.preloadPriorityFrames();
      if (window.requestIdleCallback) {
        window.requestIdleCallback(this.preloadAllFrames, { timeout: 1800 });
      } else {
        setTimeout(this.preloadAllFrames, 250);
      }
    }

    disconnectedCallback() {
      this.stopTrackingPointer();
      this.stopInertia();
      if (this.renderFrame !== null) cancelAnimationFrame(this.renderFrame);
      window.removeEventListener('pointerdown', this.onCapturedPointerDown, true);
      this.removeEventListener('click', this.onClick, true);
      this.removeEventListener('keydown', this.onKeyDown);
      this.removeEventListener('dragstart', this.onDragStart);
      document.removeEventListener('variant:update', this.reset);
      document.removeEventListener('product:variant-change', this.reset);
      this.closest('product-information')?.removeEventListener('change', this.onVariantInput);
    }

    onCapturedPointerDown = (event) => {
      if (!event.composedPath().includes(this)) return;
      event.stopImmediatePropagation();
      this.onPointerDown(event);
    };

    onPointerDown = (event) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      this.stopInertia();
      this.dragging = true;
      this.moved = false;
      this.activePointerId = event.pointerId;
      this.lastX = event.clientX;
      this.lastMoveTime = performance.now();
      this.velocity = 0;
      this.classList.add('is-dragging');
      window.addEventListener('pointermove', this.onPointerMove, { capture: true, passive: false });
      window.addEventListener('pointerup', this.onPointerUp, true);
      window.addEventListener('pointercancel', this.onPointerUp, true);
      try { this.setPointerCapture?.(event.pointerId); } catch (_) {}
    };

    onPointerMove = (event) => {
      if (!this.dragging) return;
      if (this.activePointerId !== null && event.pointerId !== this.activePointerId) return;

      const now = performance.now();
      const deltaX = event.clientX - this.lastX;
      const elapsed = Math.max(now - this.lastMoveTime, 1);
      if (Math.abs(deltaX) < 0.25) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      this.moved = true;

      if (this.frames.length === 2) {
        if (Math.abs(deltaX) >= 42) {
          this.position += deltaX < 0 ? 1 : -1;
          this.scheduleRender();
          this.lastX = event.clientX;
        }
      } else {
        const pixelsPerFrame = Math.max(8, Math.min(18, this.clientWidth / this.frames.length));
        const frameDelta = -deltaX / pixelsPerFrame;
        this.position += frameDelta;
        const instantVelocity = frameDelta / elapsed;
        this.velocity = (this.velocity * 0.68) + (instantVelocity * 0.32);
        this.lastX = event.clientX;
        this.scheduleRender();
      }

      this.lastMoveTime = now;
      this.markInteracted();
    };

    onPointerUp = (event) => {
      if (!this.dragging) return;
      event.stopImmediatePropagation();
      try { this.releasePointerCapture?.(event.pointerId); } catch (_) {}
      this.stopTrackingPointer();
      this.status.textContent = `View ${this.index + 1} of ${this.frames.length}`;
      if (
        this.frames.length > 2 &&
        !this.isProductCard &&
        !this.reduceMotion.matches &&
        Math.abs(this.velocity) > 0.004
      ) {
        this.startInertia();
      }
    };

    onClick = (event) => {
      event.stopPropagation();
      if (this.moved) event.preventDefault();
      this.moved = false;
    };

    onDragStart = (event) => event.preventDefault();

    stopTrackingPointer() {
      this.dragging = false;
      this.activePointerId = null;
      this.classList.remove('is-dragging');
      window.removeEventListener('pointermove', this.onPointerMove, true);
      window.removeEventListener('pointerup', this.onPointerUp, true);
      window.removeEventListener('pointercancel', this.onPointerUp, true);
    }

    onKeyDown = (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      this.stopInertia();
      this.position = this.index + (event.key === 'ArrowRight' ? 1 : -1);
      this.renderPosition(true);
      this.markInteracted();
    };

    onVariantInput = (event) => {
      if (event.target?.matches('input, select')) this.reset();
    };

    reset = () => {
      this.stopInertia();
      this.position = this.startIndex;
      this.renderPosition(true, true);
    };

    scheduleRender() {
      if (this.renderFrame !== null) return;
      this.renderFrame = requestAnimationFrame(() => {
        this.renderFrame = null;
        this.renderPosition(false);
      });
    }

    renderPosition(announce = false, force = false) {
      const count = this.frames.length;
      const next = ((Math.round(this.position) % count) + count) % count;
      if (next === this.index && !force) return;
      this.index = next;
      this.image.src = this.frames[next];
      this.image.alt = `${(this.dataset.productHandle || 'product').replaceAll('-', ' ')} — view ${next + 1} of ${count}`;
      if (announce) this.status.textContent = `View ${next + 1} of ${count}`;
      this.preloadPriorityFrames();
    }

    startInertia() {
      this.stopInertia();
      let previousTime = performance.now();
      const tick = (now) => {
        const elapsed = Math.min(now - previousTime, 34);
        previousTime = now;
        this.position += this.velocity * elapsed;
        this.velocity *= Math.pow(0.9, elapsed / 16.67);
        this.renderPosition(false);

        if (Math.abs(this.velocity) < 0.0012) {
          this.inertiaFrame = null;
          this.status.textContent = `View ${this.index + 1} of ${this.frames.length}`;
          return;
        }
        this.inertiaFrame = requestAnimationFrame(tick);
      };
      this.inertiaFrame = requestAnimationFrame(tick);
    }

    stopInertia() {
      if (this.inertiaFrame !== null) cancelAnimationFrame(this.inertiaFrame);
      this.inertiaFrame = null;
    }

    preloadPriorityFrames() {
      [-2, -1, 1, 2].forEach((offset) => this.preloadFrame(this.index + offset));
    }

    preloadAllFrames = () => {
      this.frames.forEach((_, index) => this.preloadFrame(index));
    };

    preloadFrame(index) {
      const normalized = ((index % this.frames.length) + this.frames.length) % this.frames.length;
      if (this.preloaded.has(normalized)) return;
      const source = this.frames[normalized];
      if (!this.frameCache.has(source)) {
        const image = new Image();
        image.decoding = 'async';
        image.src = source;
        this.frameCache.set(source, image);
      }
      this.preloaded.add(normalized);
    }

    markInteracted() {
      this.classList.add('has-interacted');
    }
  });
}
